import { Service } from 'components/system/classes/angular-entities';
import { A9Service } from 'components/system/angular/A9Service';
import { CustomerParamsDataProvider } from './customer-params.data-provider';
import { FullNameEnum, RsCustomerParamsInfo } from './types';
import { IEventEmitter, ItemResource } from 'components/system';
import { lowerCaseFirstLetter } from 'components/utils';
import { toCanonicalName } from './customer-name-utils';

const events = {
    update: 'update'
};

type CustomerParams = {
    id: number;
    fullName1: FullNameEnum;
    fullName2: FullNameEnum;
    fullName3: FullNameEnum;
    fullName4: FullNameEnum;
    fullName5: FullNameEnum;
    fullName6: FullNameEnum;
    fullName7: FullNameEnum;
    fullName8: FullNameEnum;
    fullName9: FullNameEnum;
};

const fullNameFields = ['fullName1', 'fullName2', 'fullName3', 'fullName4', 'fullName5', 'fullName6', 'fullName7', 'fullName8', 'fullName9'];

export interface ICustomerParamsService extends IEventEmitter {
    ready: Promise<void>;
    params: string[];
    keyUpdate: string;
    init(): Promise<void>;
    toValidName(name: string): string;
}

@A9Service('customerParamsService')
export class CustomerParamsService extends Service implements ICustomerParamsService {
    private config: RsCustomerParamsInfo;
    private promise: Promise<void>;
    private dataProvider: CustomerParamsDataProvider;

    constructor() {
        super();
        this.promise = null;
        this.dataProvider = CustomerParamsDataProvider.getInstance();
    }

    public get ready(): Promise<void> {
        return this.promise;
    }

    public get params(): string[] {
        const fields: string[] = [];
        Object.keys(this.config).forEach((key: string) => {
            if (fullNameFields.indexOf(key) !== -1) {
                if (this.config[key] !== FullNameEnum.None) {
                    fields.push(lowerCaseFirstLetter(this.config[key]));
                }
            }
        });
        return fields; // array of FullNameEnum with  lower case first letter;
    }

    public get keyUpdate(): string {
        return events.update;
    }

    public init(): Promise<void> {
        if (!this.promise) {
            this.promise = new Promise((resolve, _reject) => {
                const resource: ItemResource<RsCustomerParamsInfo> = this.dataProvider.getItemResource({});
                resource.onUpdated(() => {
                    this.promise = null;
                    resource.reload();
                });
                resource.onLoaded((item: RsCustomerParamsInfo) => {
                    this.config = item || null;
                    this.emit(this.keyUpdate);
                    resolve();
                });
                resource.reload();
            });
        }
        return this.promise;
    }

    public toValidName(name: string): string {
        if (!this.config || !this.config.checkNames || !name || name.length < 2) {
            return name;
        }

        /*
        Each name (last and first) checked and improved in next way:
        Only names longer that 2 characters checked
        If no spaces - converted to Canonical name (see below) - FIRST -> First, first -> First
        If 1 space - both converted to Canonical name - FIRST NAME -> First Name, first name -> First Name
        If more than 1 space - last and first converted, all middle parts untouched - FIRST LONG NAME -> First LONG Name, first long name -> First long Name, first long LONG name -> First long LONG Name
        Canonical name - each delimiter cause capital letter. Delimeters - ".", "-". F-IRST -> F-Irst, F.IRST -> F.Irst
        Extreme case - f-irst loNG l.ong name -> F-Irst loNG l.ong Name
         */

        const parts = name.split(' ').filter(val => !!val);
        if (!parts.length) {
            return '';
        }
        parts[0] = this.toCanonicalName(parts[0]);
        if (parts.length > 1 && parts[parts.length - 1]) {
            parts[parts.length - 1] = this.toCanonicalName(parts[parts.length - 1]);
        }
        return parts.join(' ');
    }

    private toCanonicalName(str: string): string {
        return toCanonicalName(str);
    }
}
